import { flux } from '@/core/flux.module';
import { PUSH_IGNORE, PUSH_WORKBENCH } from '@/core/flux.service';
import {
  AgentType,
  AnswerStyle,
  ChatAttachment,
  ChatMessage,
  FontSize,
  ReasoningStyle,
  SelectedDocument,
  AIMemory,
} from '@/aiAssistant/aiAssistant.types';

export function setAIAssistantDisplayed(displayed: boolean, ignore = false) {
  flux.dispatch('AI_ASSISTANT_DISPLAYED', { displayed }, ignore ? PUSH_IGNORE : PUSH_WORKBENCH);
}

export function setAIAssistantMaximized(maximized: boolean) {
  flux.dispatch('AI_ASSISTANT_MAXIMIZED', { maximized }, PUSH_WORKBENCH);
}

export function setAIAssistantMessages(messages: ChatMessage[]) {
  flux.dispatch('AI_ASSISTANT_SET_MESSAGES', { messages }, PUSH_WORKBENCH);
}

export function setSelectedAgent(agent: AgentType) {
  flux.dispatch('AI_ASSISTANT_SET_SELECTED_AGENT', { agent }, PUSH_WORKBENCH);
}

export function setAIAssistantFontSize(fontSize: FontSize) {
  flux.dispatch('AI_ASSISTANT_SET_FONT_SIZE', { fontSize }, PUSH_WORKBENCH);
}
export function setAIAssistantExternalPrompt(
  payload: { prompt: string; agent?: AgentType; insertOnly?: boolean } | undefined,
) {
  flux.dispatch('AI_ASSISTANT_EXTERNAL_PROMPT', payload);
  if (payload?.agent) {
    setSelectedAgent(payload.agent);
  }
}
export function setAIAssistantAnswerStyle(answerStyle: AnswerStyle) {
  flux.dispatch('AI_ASSISTANT_SET_ANSWER_STYLE', { answerStyle }, PUSH_WORKBENCH);
}

export function setAIAssistantReasoningStyle(reasoningStyle: ReasoningStyle) {
  flux.dispatch('AI_ASSISTANT_SET_REASONING_STYLE', { reasoningStyle }, PUSH_WORKBENCH);
}

export function setAIAssistantGenerateMemory(generateMemory: boolean) {
  flux.dispatch('AI_ASSISTANT_SET_GENERATE_MEMORY', { generateMemory }, PUSH_WORKBENCH);
}

/**
 * Sets the preferred subsection of the AI preferences tab
 * Mainly for the redirecting of memories in the memory ui
 * @param subsection The subsection to set
 */
export function setAIAssistantPreferredSubsection(subsection: 'general' | 'memories' | 'privacy' | null) {
  flux.dispatch('AI_ASSISTANT_SET_PREFERRED_SUBSECTION', { subsection }, PUSH_WORKBENCH);
}

export function setAIAssistantSavedMemories(savedMemories: AIMemory[]) {
  flux.dispatch('AI_ASSISTANT_SET_SAVED_MEMORIES', { savedMemories }, PUSH_WORKBENCH);
}

export function addAIAssistantMemory(memory: AIMemory) {
  flux.dispatch('AI_ASSISTANT_ADD_MEMORY', { memory }, PUSH_WORKBENCH);
}

export function removeAIAssistantMemory(memoryId: string) {
  flux.dispatch('AI_ASSISTANT_REMOVE_MEMORY', { memoryId }, PUSH_WORKBENCH);
}

export function updateAIAssistantMemory(memory: AIMemory) {
  flux.dispatch('AI_ASSISTANT_UPDATE_MEMORY', { memory }, PUSH_WORKBENCH);
}

export function setAiAssistantRefreshHistory(refreshHistory: boolean) {
  flux.dispatch('AI_ASSISTANT_SET_REFRESH_HISTORY', { refreshHistory });
}

export function setAiAssistantActiveChat(chat: { id: string; name: string } | undefined) {
  flux.dispatch('AI_ASSISTANT_SET_ACTIVE_CHAT', { chat });
}

export function setAiAssistantHistorySearch(searchValue: string) {
  flux.dispatch('AI_ASSISTANT_SET_HISTORY_SEARCH', { searchValue }, PUSH_WORKBENCH);
}

export function setAIAssistantProcessingExternalInput(value: 'running' | 'completed' | 'stopped') {
  flux.dispatch('AI_ASSISTANT_SET_PROCESSING_EXTERNAL_INPUT', { value }, PUSH_WORKBENCH);
}

/**
 * Sets the selected document to reference in the AI Assistant
 * @param document The document to reference or null to clear
 */
export function setSelectedDocument(document: SelectedDocument | SelectedDocument[] | null) {
  flux.dispatch('AI_ASSISTANT_SET_SELECTED_DOCUMENT', { document });
}

/**
 * Adds a draft attachment to the AI Assistant
 * @param attachment The attachment to add
 */
export function addDraftAttachment(attachment: ChatAttachment) {
  flux.dispatch('AI_ASSISTANT_ADD_DRAFT_ATTACHMENT', { attachment });
}

/**
 * Removes a draft attachment from the AI Assistant
 * @param attachmentId The ID of the attachment to remove
 */
export function removeDraftAttachment(attachmentId: string) {
  flux.dispatch('AI_ASSISTANT_REMOVE_DRAFT_ATTACHMENT', { attachmentId });
}

/**
 * Clears all draft attachments from the AI Assistant
 */
export function clearDraftAttachments() {
  flux.dispatch('AI_ASSISTANT_CLEAR_DRAFT_ATTACHMENTS', {});
}
